import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Plus, Sheet as SheetIcon, Trash2 } from 'lucide-react';
import SheetKanbanView from './SheetKanbanView';
import SheetToolbar from './SheetToolbar';
import SheetShareModal from './SheetShareModal';
import SheetPlanSettings from './SheetPlanSettings';

const ACCENT = '#667eea';
const TEXT_TITLE = '#1a1a2e';
const TEXT_BODY = '#334155';
const TEXT_MUTED = '#64748b';
const TEXT_FAINT = '#94a3b8';
const BORDER = '#e2e8f0';
const SURFACE = '#ffffff';
const PAGE_BG = '#f5f7fb';

function SheetsManager({ user, workspaceId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSheetId = searchParams.get('id');
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [activeSheetId, setActiveSheetIdState] = useState(urlSheetId ? parseInt(urlSheetId, 10) : null);

  // Keep URL ?id= in sync with the active sheet so a link to /kanban?id=N
  // reopens the same plan.
  const setActiveSheetId = useCallback((id) => {
    setActiveSheetIdState(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('id', String(id));
      else next.delete('id');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [loading, setLoading] = useState(true);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '' });
  const [creating, setCreating] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPlanSettings, setShowPlanSettings] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const [cardSearchQuery, setCardSearchQuery] = useState('');

  const showError = useCallback((msg) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(null), 4000);
  }, []);

  const loadSheets = useCallback(async () => {
    try {
      const res = await axios.get('/api/sheets');
      setSheets(Array.isArray(res.data) ? res.data : (res.data.sheets || []));
    } catch (err) {
      console.error('Failed to load sheets:', err);
      showError('Failed to load plans. Please refresh.');
    }
  }, [showError]);

  const loadSheet = useCallback(async (sheetId) => {
    if (!sheetId) return;
    setLoadingSheet(true);
    try {
      const res = await axios.get(`/api/sheets/${sheetId}`);
      setActiveSheet(res.data);
    } catch (err) {
      console.error('Failed to load sheet:', err);
      showError('Failed to load plan.');
    } finally {
      setLoadingSheet(false);
    }
  }, [showError]);

  useEffect(() => {
    loadSheets().finally(() => setLoading(false));
  }, [loadSheets, workspaceId]);

  // Sync activeSheetId with the ?id= URL param (e.g. when navigated to from Planner)
  useEffect(() => {
    const idFromUrl = urlSheetId ? parseInt(urlSheetId, 10) : null;
    if (idFromUrl !== activeSheetId) {
      setActiveSheetIdState(idFromUrl);
    }
  }, [urlSheetId, activeSheetId]);

  useEffect(() => {
    if (activeSheetId) loadSheet(activeSheetId);
    else setActiveSheet(null);
  }, [activeSheetId, loadSheet]);

  const createSheet = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post('/api/sheets', { name: createForm.name.trim() });
      await loadSheets();
      setActiveSheetId(res.data.id);
      setShowCreateModal(false);
      setCreateForm({ name: '' });
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to create plan.');
    } finally {
      setCreating(false);
    }
  };

  const renameSheet = async (name) => {
    await axios.put(`/api/sheets/${activeSheet.id}`, { name });
    await loadSheets();
    await loadSheet(activeSheet.id);
  };

  const deleteSheet = async (sheetId) => {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/sheets/${sheetId}`);
      if (activeSheetId === sheetId) setActiveSheetId(null);
      await loadSheets();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to delete plan.');
    }
  };

  const handleSheetUpdated = (updatedSheet) => {
    setActiveSheet((prev) => prev ? { ...prev, ...updatedSheet } : prev);
    loadSheets();
  };

  const handlePlanSettingsClose = ({ deleted } = {}) => {
    setShowPlanSettings(false);
    if (deleted) {
      setActiveSheetId(null);
      loadSheets();
    }
  };

  const filteredPlans = useMemo(() => {
    const q = planSearchQuery.trim().toLowerCase();
    if (!q) return sheets;
    return sheets.filter((s) => (s.name || '').toLowerCase().includes(q));
  }, [sheets, planSearchQuery]);

  // Filter rows by card title search before passing to Kanban
  const visibleSheet = useMemo(() => {
    if (!activeSheet) return null;
    const q = cardSearchQuery.trim().toLowerCase();
    if (!q) return activeSheet;
    const titleColId = activeSheet.title_column_id;
    if (!titleColId) return activeSheet;
    const visibleRowIds = new Set();
    for (const row of activeSheet.rows || []) {
      const cell = activeSheet.cells?.[row.id]?.[titleColId];
      const title = cell?.display_val != null ? String(cell.display_val) : '';
      if (title.toLowerCase().includes(q)) visibleRowIds.add(row.id);
    }
    return {
      ...activeSheet,
      rows: (activeSheet.rows || []).filter((r) => visibleRowIds.has(r.id))
    };
  }, [activeSheet, cardSearchQuery]);

  if (loading) {
    return <div data-fullwidth="true" style={{ padding: '40px', textAlign: 'center', color: TEXT_MUTED }}>Loading plans…</div>;
  }

  return (
    <div data-fullwidth="true" style={{ display: 'flex', height: '100%', background: PAGE_BG }}>
      {/* Left rail: plan list */}
      <aside style={{ width: '260px', flexShrink: 0, background: SURFACE, borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', borderBottom: `1px solid ${BORDER}` }}>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px 14px', background: ACCENT, color: 'white', border: 'none', borderRadius: '999px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}
          >
            <Plus size={16} /> New plan
          </button>
          <input
            type="search"
            placeholder="Search plans…"
            value={planSearchQuery}
            onChange={(e) => setPlanSearchQuery(e.target.value)}
            style={{ marginTop: '10px', width: '100%', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${BORDER}`, fontSize: '0.85rem', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {filteredPlans.length === 0 && (
            <div style={{ padding: '20px 10px', textAlign: 'center', color: TEXT_FAINT, fontSize: '0.85rem' }}>No plans match.</div>
          )}
          {filteredPlans.map((s) => (
            <div
              key={s.id}
              onClick={() => setActiveSheetId(s.id)}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', background: activeSheetId === s.id ? '#eef2ff' : 'transparent', color: activeSheetId === s.id ? '#3730a3' : TEXT_BODY, marginBottom: '2px' }}
            >
              <SheetIcon size={14} color={activeSheetId === s.id ? '#6366f1' : TEXT_FAINT} />
              <span style={{ flex: 1, fontSize: '0.88rem', fontWeight: activeSheetId === s.id ? 600 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); deleteSheet(s.id); }} title="Delete plan" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '2px', display: 'flex', opacity: 0.6 }}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Right: board */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {errorMessage && (
          <div style={{ padding: '10px 24px', background: '#fef2f2', color: '#b91c1c', borderBottom: '1px solid #fecaca', fontSize: '0.85rem' }}>{errorMessage}</div>
        )}

        {!activeSheet && !loadingSheet && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
              <SheetIcon size={48} color={ACCENT} style={{ marginBottom: '16px' }} />
              <h2 style={{ margin: 0, fontStyle: 'italic', color: TEXT_TITLE }}>No plan selected</h2>
              <p style={{ marginTop: '8px', color: TEXT_MUTED, fontSize: '0.9rem' }}>
                {sheets.length === 0 ? 'Create your first plan to get started.' : 'Pick a plan from the left, or create a new one.'}
              </p>
            </div>
          </div>
        )}

        {loadingSheet && (
          <div style={{ padding: '40px', textAlign: 'center', color: TEXT_MUTED }}>Loading plan…</div>
        )}

        {activeSheet && !loadingSheet && (
          <>
            <SheetToolbar
              sheet={activeSheet}
              role={activeSheet.role}
              onRenameSheet={renameSheet}
              onOpenShare={() => setShowShareModal(true)}
              onOpenSettings={() => setShowPlanSettings(true)}
              searchQuery={cardSearchQuery}
              onSearchChange={setCardSearchQuery}
            />
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <SheetKanbanView
                sheet={visibleSheet}
                user={user}
                onReload={() => loadSheet(activeSheet.id)}
                onSheetUpdated={handleSheetUpdated}
              />
            </div>
          </>
        )}
      </main>

      {/* Create plan modal */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ width: '100%', maxWidth: '440px', background: SURFACE, borderRadius: '14px', padding: '28px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.15rem', fontWeight: 700, color: TEXT_TITLE }}>New plan</h3>
            <form onSubmit={createSheet} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                autoFocus
                value={createForm.name}
                onChange={(e) => setCreateForm({ name: e.target.value })}
                placeholder="Plan name (e.g. Q3 Launch)"
                style={{ padding: '10px 12px', borderRadius: '8px', border: `1px solid ${BORDER}`, fontSize: '0.95rem' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                <button type="button" onClick={() => setShowCreateModal(false)} disabled={creating} style={{ padding: '8px 16px', background: 'transparent', color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: '999px', fontSize: '0.9rem', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={creating} style={{ padding: '8px 18px', background: ACCENT, color: 'white', border: 'none', borderRadius: '999px', fontSize: '0.9rem', fontWeight: 600, cursor: creating ? 'not-allowed' : 'pointer' }}>{creating ? 'Creating…' : 'Create plan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share modal */}
      {showShareModal && activeSheet && (
        <SheetShareModal
          sheet={activeSheet}
          onClose={() => setShowShareModal(false)}
          onChange={() => loadSheet(activeSheet.id)}
        />
      )}

      {/* Plan Settings panel */}
      {showPlanSettings && activeSheet && (
        <SheetPlanSettings
          sheet={activeSheet}
          onClose={handlePlanSettingsClose}
          onSheetUpdated={handleSheetUpdated}
          onColumnsChanged={() => loadSheet(activeSheet.id)}
        />
      )}
    </div>
  );
}

export default SheetsManager;
